import Vue from 'vue';
import { Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { capitalize } from '@/utils';
import { client } from '@/store';
import { Access } from '@/models';

export abstract class BaseDetail<
  Entity extends { id: string | number }
  > extends Vue {
  @Prop(String) readonly id?: number;
  @Prop(Boolean) readonly useProp?: boolean;
  @Prop(String) readonly title?: string;
  @Getter('modules', { namespace: 'user' }) moduleAccess: Record<string, string>;
  entityPatch: Partial<Entity> = {};
  client = client;
  loading = false;
  hide = {};
  setValue = Vue.set;
  abstract namespace: string;
  get entityId() {
    return this.useProp ? this.id && String(this.id) : this.$route.params.id;
  }
  get baseEntity(): Partial<Entity> {
    const id = this.entityId;
    const baseEntity = id ? this.entities[id] : {};
    return baseEntity || {};
  }
  get entity(): Partial<Entity> {
    return { ...this.baseEntity, ...this.entityPatch };
  }
  get entities(): Record<string, Entity> {
    return this.$store.state[this.namespace]?.entities || {};
  }
  get isCreating() {
    return !Number(this.entityId);
  }
  get isDisabled() {
    const modules = this.$store.getters['user/modules'] || {};
    return !modules[capitalize(this.namespace)]?.includes(Access.Update);
  }

  get set() {
    // tslint:disable-next-line: no-empty
    return this.isDisabled ? Vue.set : () => { };
  }
  setVal(prop: string, pickId = false) {
    return (val: any) => {
      const value = val === undefined ? null : val;
      Vue.set(this.entityPatch, prop, pickId ? value.id : value);
    };
  }

  get permissions() {
    if (!this.$route.name) return undefined;
    else {
      const resource = /(\w+?)[A-Z]+/g.exec(this.$route.name)?.[1] || '';
      return this.moduleAccess[resource];
    }
  }
  get canCreate() {
    return this.permissions?.includes(Access.Create) || false;
  }
  get canEdit() {
    return this.permissions?.includes(Access.Update) || false;
  }
  get canRead() {
    return this.permissions?.includes(Access.Read) || false;
  }
  get hasAccess() {
    return this.$route.name?.includes('Create') ? this.canCreate : this.canEdit || this.canRead;
  }

  getEntity(id: string): Promise<Entity> {
    return this.$store.dispatch(`${this.namespace}/getEntity`, id);
  }
  createEntity(entity: Partial<Entity>): Promise<Entity> {
    return this.$store.dispatch(`${this.namespace}/createEntity`, entity);
  }
  updateEntity(entity: Partial<Entity>): Promise<Entity> {
    return this.$store.dispatch(`${this.namespace}/updateEntity`, entity);
  }
  deleteEntity(entity: Partial<Entity>): Promise<Entity> {
    return this.$store.dispatch(`${this.namespace}/deleteEntity`, entity);
  }

  loadEntity(): Promise<void | Partial<Entity>> {
    if (this.isCreating) return Promise.resolve();
    return this.getEntity(String(this.entityId)).then((entity) => {
      Vue.set(this.entityPatch, 'id', Number(this.entityId));
      return entity;
    });
  }
  create(redirect = true) {
    this.loading = true;
    this.createEntity(this.entity)
      .then((response) => {
        // @ts-ignore
        const entity = response.data || response
        if (this.useProp) {
          this.$emit('create', { entity, redirect });
        } else {
          if (redirect) {
            // @ts-ignore
            this.$router.push({ name: `${capitalize(this.namespace)}Detail`, params: { id: entity.id } });
          }
        }
        this.entityPatch = {};
        this.loading = false;
      })
      .catch(() => (this.loading = false));
  }
  update(redirect = false) {
    this.loading = true;
    this.updateEntity(this.entityPatch)
      .then((entity) => {
        this.entityPatch.id = Number(this.entityId)
        this.loading = false;
        if (this.useProp) {
          this.$emit('change', { entity });
        } else {
          if (redirect) {
            this.$router.push({ name: `${capitalize(this.namespace)}List` });
          }
        }
      })
      .catch(() => (this.loading = false));
  }
  remove() {
    this.deleteEntity(this.entity).then(() => {
      if (this.useProp) {
        this.$emit('remove', { entity: this.entity });
      } else {
        this.$router.push({ name: `${capitalize(this.namespace)}List` });
      }
    });
  }
}
